import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { WarningSharp } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';
import { Button } from '@uplift/ui-v2';
import { UnsubmittedSessionRow } from '../../types';
import { useStyles } from './styles';
import { CalendarDateService, l10nEnService } from '@uplift/services';
import { DateFormats, TimeFormats } from '@uplift/constants';
import { useUnsubmittedSessionsContent } from '../../context';

export type UnsubmittedSessionCardProps = {
  row: UnsubmittedSessionRow;
  style?: React.CSSProperties;
};

const getFormattedFromDatetime = (fromDatetime: string, l10n: typeof l10nEnService) => {
  const dayjsDate = CalendarDateService.date(fromDatetime);
  const date = dayjsDate.format(DateFormats['MM/DD/YY']);
  const time = dayjsDate.format(TimeFormats['h:mm a']);

  return l10n.clinicalNotes.nextAppointment(date, time);
};

const UnsubmittedSessionCard_: React.FC<UnsubmittedSessionCardProps> = ({
  row: {
    appointmentState,
    appointmentId,
    patientName,
    fromDatetime
  }
}) => {
  const { onSubmitSession, onToggleCancelSessionModal } = useUnsubmittedSessionsContent();
  const { classes } = useStyles();
  const l10n = useL10n();
  const formattedFromDateTime = getFormattedFromDatetime(fromDatetime, l10n);
  const isExpired = appointmentState === 'expired';
  const appointmentType = l10n.appointments.type.therapy;
  const duration = l10n.appointments.duration.sixtyShort;

  return (
    <Paper elevation={2}>
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography className={classes.fontWeight600}>{patientName}</Typography>
          {isExpired && (
            <Box display="flex">
              <WarningSharp className={classes.icon} />
            </Box>
          )}
        </Box>
        <Box mb={2} display="flex" alignItems="center" className={classes.detailsLine}>
          <Typography color="textPrimary">{formattedFromDateTime}&nbsp;</Typography>
          <Typography color="textSecondary" component="span">{'('}{appointmentType}&nbsp;{duration}{')'}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap="0.5rem">
          <Button
            variant="contained"
            onClick={() => onSubmitSession(appointmentId)}
            disabled={isExpired}
            color="secondary"
            fullWidth
            size="small"
          >
            {l10n.general.submit}
          </Button>
          <Button
            variant="outlined"
            onClick={() => onToggleCancelSessionModal(appointmentId)}
            fullWidth
            size="small"
          >
            {l10n.general.cancel}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export const UnsubmittedSessionCard = React.memo(UnsubmittedSessionCard_);
